import { useEffect, type ReactNode } from 'react';
import { ArrowDown, ArrowRight, Coffee, Heart, Mail, MapPin, Music2, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

const mailtoMusician = 'mailto:hello@thebondcoffee.com?subject=Bond%20Sessions%20-%20Artist%20Inquiry';
const coffeeStillLife = 'https://images.pexels.com/photos/8511267/pexels-photo-8511267.jpeg?auto=compress&cs=tinysrgb&h=650&w=940';
const coffeeBeans = 'https://images.pexels.com/photos/5171115/pexels-photo-5171115.jpeg?auto=compress&cs=tinysrgb&h=650&w=940';
const guitarPhoto = 'https://images.pexels.com/photos/6577088/pexels-photo-6577088.jpeg?auto=compress&cs=tinysrgb&h=650&w=940';

function Eyebrow({ children, light = false }: { children: ReactNode; light?: boolean }) {
  return (
    <div className={`flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.2em] ${light ? 'text-[#fda36b]' : 'text-[#f08b13]'}`}>
      <span className={`h-px w-8 ${light ? 'bg-[#fda36b]' : 'bg-[#f08b13]'}`} />
      {children}
    </div>
  );
}

export default function BondSessions() {
  useEffect(() => {
    document.title = 'Bond Sessions | Bond Coffee Roasters';
    const robots = document.querySelector('meta[name="robots"]') ?? document.createElement('meta');
    robots.setAttribute('name', 'robots');
    robots.setAttribute('content', 'noindex, nofollow');
    document.head.appendChild(robots);

    return () => {
      document.title = 'Bond Coffee Roasters';
      robots.remove();
    };
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#f7f4ee] text-[#132947]">
      <header className="sticky top-0 z-30 border-b border-[#132947]/10 bg-[#f7f4ee]/90 px-5 py-4 backdrop-blur-xl lg:px-10">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <Link to="/" aria-label="Visit Bond Coffee Roasters home" className="group flex items-center gap-3">
            <span className="font-serif text-2xl font-black tracking-[-0.08em] text-[#132947] sm:text-3xl">
              BOND<span className="text-[#fda36b]">.</span>
            </span>
            <span className="hidden border-l border-[#132947]/20 pl-3 text-[9px] font-black uppercase leading-tight tracking-[0.16em] text-[#132947]/65 sm:block">
              Coffee<br />Roasters
            </span>
          </Link>
          <a href="#connect" className="group inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.15em] transition hover:text-[#f08b13] sm:text-xs">
            Join a Session
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
          </a>
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden bg-[#05090e] px-5 py-14 text-white sm:py-20 lg:py-28">
          <div className="pointer-events-none absolute -right-32 -top-40 h-[28rem] w-[28rem] rounded-full bg-[#fda36b]/10 blur-3xl" />
          <div className="pointer-events-none absolute inset-0 opacity-20 [background-image:radial-gradient(#fda36b_1px,transparent_1px)] [background-size:28px_28px]" />
          <div className="relative mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:gap-20">
            <div className="max-w-2xl">
              <div className="mb-7 flex items-center gap-3 text-[#fda36b]">
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[#fda36b]/40 bg-[#fda36b]/10"><Coffee className="h-4 w-4" /></span>
                <span className="text-[10px] font-black uppercase tracking-[0.24em]">Bond Sessions / 01</span>
              </div>
              <h1 className="font-serif text-[3.25rem] font-black leading-[0.91] tracking-[-0.06em] sm:text-6xl lg:text-[5.7rem]">
                Good coffee.<br />
                Great music.<br />
                <span className="text-[#fda36b]">Stories worth sharing.</span>
              </h1>
              <p className="mt-7 max-w-md font-serif text-xl italic leading-snug text-white/65 sm:text-2xl">
                One roast. One conversation. One song.
              </p>
              <a href="#connect" className="mt-9 inline-flex items-center gap-3 rounded-full bg-[#fda36b] px-6 py-4 text-xs font-black uppercase tracking-[0.15em] text-[#132947] transition duration-300 hover:-translate-y-1 hover:bg-[#ffbd8d] sm:px-7 sm:text-sm">
                Pull Up a Chair
                <ArrowDown className="h-4 w-4" />
              </a>
            </div>
            <div className="relative mx-auto w-full max-w-lg lg:ml-auto">
              <div className="absolute -inset-3 rounded-[2rem] border border-[#fda36b]/30" />
              <div className="relative overflow-hidden rounded-[1.5rem] bg-[#132947] p-2 shadow-2xl shadow-black/30">
                <img src={coffeeStillLife} alt="Espresso surrounded by freshly roasted coffee beans" className="aspect-[4/3] w-full rounded-[1.1rem] object-cover" />
                <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between rounded-xl border border-white/15 bg-[#05090e]/75 p-4 backdrop-blur-md">
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.18em] text-[#fda36b]">A Bond original</p>
                    <p className="mt-1 font-serif text-lg font-bold text-white">Coffee, made personal.</p>
                  </div>
                  <Music2 className="h-5 w-5 text-[#fda36b]" />
                </div>
              </div>
            </div>
          </div>
          <div className="relative mx-auto mt-14 flex max-w-6xl items-center gap-4 text-[9px] font-black uppercase tracking-[0.18em] text-white/40">
            <span className="h-px flex-1 bg-white/15" />
            <span>North Shore, Massachusetts</span>
            <span className="h-px flex-1 bg-white/15" />
          </div>
        </section>

        <section className="bg-[#f7f4ee] px-5 py-16 sm:py-24 lg:py-28">
          <div className="mx-auto max-w-3xl text-center">
            <Eyebrow>Start with a cup</Eyebrow>
            <p className="mt-7 font-serif text-2xl font-bold leading-tight tracking-[-0.03em] sm:text-4xl">
              Some of life's best moments are the simplest ones.
            </p>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-[#132947]/70 sm:text-lg">
              A great cup of coffee. A song that hits just right. A conversation that goes somewhere you didn't expect. That's what Bond Sessions is all about.
            </p>
            <div className="mt-10 grid gap-3 text-left sm:grid-cols-3">
              {[
                ['01', 'Roast together', 'A relaxed afternoon at Bond HQ.'],
                ['02', 'Share a story', 'The people and places behind the music.'],
                ['03', 'Play a song', 'Something original, meaningful, or just felt.'],
              ].map(([number, title, text]) => (
                <div key={number} className="rounded-2xl border border-[#132947]/10 bg-white/60 p-5 transition hover:-translate-y-1 hover:border-[#fda36b]/60">
                  <span className="text-[10px] font-black tracking-[0.18em] text-[#f08b13]">{number}</span>
                  <p className="mt-5 font-serif text-lg font-bold">{title}</p>
                  <p className="mt-2 text-sm leading-relaxed text-[#132947]/60">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white px-5 py-16 sm:py-24 lg:py-28">
          <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-20">
            <div>
              <Eyebrow>How it started</Eyebrow>
              <h2 className="mt-6 max-w-xl font-serif text-4xl font-black leading-[0.94] tracking-[-0.05em] sm:text-5xl lg:text-6xl">
                Coffee was always about more than coffee.
              </h2>
              <div className="mt-7 max-w-xl space-y-5 text-base leading-relaxed text-[#132947]/70 sm:text-lg">
                <p>When we started Bond Coffee, we didn't just want to make great coffee. We wanted to bring people together.</p>
                <p>We're Chris and Adam. Neighbors, friends, coffee guys, and lifelong music lovers.</p>
                <p>Some of our favorite moments involve a good cup of coffee, great music playing in the background, and a conversation that somehow turns into three hours.</p>
                <p>Music and coffee have a lot in common. They make you slow down. They bring back memories. They connect people who might otherwise never cross paths.</p>
                <p>So we thought, why not bring them together?</p>
                <p className="font-serif text-xl font-bold text-[#132947]">That's how Bond Sessions was born.</p>
              </div>
            </div>
            <div className="relative mx-auto w-full max-w-xl">
              <div className="absolute -inset-3 rounded-[2rem] bg-[#fda36b]/20" />
              <img src={coffeeStillLife} alt="Espresso surrounded by freshly roasted coffee beans" className="relative aspect-[4/3] w-full rounded-[1.5rem] object-cover shadow-[0_24px_50px_rgba(19,41,71,0.16)]" loading="lazy" />
              <div className="absolute -bottom-5 -left-3 flex items-center gap-3 rounded-xl bg-[#132947] px-4 py-3 text-white shadow-xl sm:left-5">
                <Sparkles className="h-4 w-4 text-[#fda36b]" />
                <span className="text-xs font-bold">Made for good company</span>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#132947] px-5 py-16 text-white sm:py-24 lg:py-28">
          <div className="mx-auto max-w-6xl">
            <div className="max-w-2xl">
              <Eyebrow light>The afternoon</Eyebrow>
              <h2 className="mt-6 font-serif text-4xl font-black leading-[0.94] tracking-[-0.05em] sm:text-5xl lg:text-6xl">Come roast with us.<br /><span className="text-[#fda36b]">Stay for a song.</span></h2>
            </div>
            <div className="mt-12 grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 sm:p-8">
                <Coffee className="h-7 w-7 text-[#fda36b]" />
                <p className="mt-6 text-base leading-relaxed text-white/70 sm:text-lg">We're inviting musicians into Bond HQ for something a little different.</p>
                <p className="mt-5 text-base leading-relaxed text-white/70 sm:text-lg">No stage lights. No big production. No awkward interview with 27 questions.</p>
                <p className="mt-5 text-base leading-relaxed text-white/70 sm:text-lg">Just a relaxed afternoon hanging out, roasting some coffee, talking about life, and sharing music.</p>
              </div>
              <div>
                <p className="text-base leading-relaxed text-white/70 sm:text-lg">We'll roast together, make some coffee, and ask you one question:</p>
                <div className="mt-7 border-l-2 border-[#fda36b] pl-5 sm:pl-7">
                  <p className="font-serif text-2xl font-bold leading-snug text-[#fda36b] sm:text-3xl">If you could have coffee with anyone, living or dead, who would it be?</p>
                </div>
                <div className="mt-7 space-y-5 text-base leading-relaxed text-white/70 sm:text-lg">
                  <p>Maybe it's someone you miss. Someone who inspired you. Someone you've always wanted to meet. Or someone who would just make for one hell of a conversation.</p>
                  <p>Then, when the coffee's ready, we'd love for you to play us a song. Something original. Something meaningful. Something you just feel like playing.</p>
                  <p className="font-serif text-xl font-bold text-white">Because good music and good stories deserve to be heard.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#fda36b] px-5 py-16 sm:py-24 lg:py-28">
          <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:gap-20">
            <div className="relative order-2 lg:order-1">
              <div className="absolute -inset-3 rounded-[2rem] bg-white/25" />
              <img src={coffeeBeans} alt="Hands holding freshly roasted coffee beans" className="relative aspect-[4/3] w-full rounded-[1.5rem] object-cover shadow-[0_24px_50px_rgba(19,41,71,0.2)]" loading="lazy" />
            </div>
            <div className="order-1 lg:order-2">
              <div className="mb-5 flex items-center gap-3 text-[#132947]"><Heart className="h-5 w-5" /><Eyebrow>Your collaboration</Eyebrow></div>
              <h2 className="font-serif text-4xl font-black leading-[0.94] tracking-[-0.05em] text-[#132947] sm:text-5xl lg:text-6xl">Your music.<br />Your coffee.<br />Your cause.</h2>
              <div className="mt-7 max-w-xl space-y-5 text-base leading-relaxed text-[#132947]/75 sm:text-lg">
                <p>Here's where things get especially fun.</p>
                <p>Together, we'll create a limited-edition Bond Coffee blend inspired by you. You help us name it, shape the vibe, and tell its story. We'll handle the coffee, roasting, packaging, and fulfillment.</p>
                <p>And because we believe good things should do some good, we'll work with you to select a charity or cause close to your heart.</p>
                <p>Your fans get a great bag of coffee with a story behind it. Your music reaches new ears. And together, we get to put a little good into the world.</p>
                <p className="font-serif text-xl font-bold text-[#132947]">Not a bad afternoon's work.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white px-5 py-16 sm:py-24 lg:py-28">
          <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-20">
            <div>
              <Eyebrow>The community</Eyebrow>
              <h2 className="mt-6 font-serif text-4xl font-black leading-[0.94] tracking-[-0.05em] sm:text-5xl lg:text-6xl">Local music deserves a bigger stage.</h2>
              <div className="mt-7 max-w-xl space-y-5 text-base leading-relaxed text-[#132947]/70 sm:text-lg">
                <p>We're starting right here on the North Shore of Massachusetts, where there is no shortage of ridiculously talented people making music.</p>
                <p>Singer-songwriters. Folk artists. Rock musicians. Jazz players. Established performers. People who are just getting started.</p>
                <p>You don't need a record deal, a huge following, or a blue checkmark. You just need a love of music, a story to share, and a willingness to hang out over coffee.</p>
              </div>
              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 font-serif text-lg font-bold text-[#132947]"><span>We'll supply the beans.</span><span className="text-[#f08b13]">You bring the music.</span></div>
            </div>
            <div className="relative mx-auto w-full max-w-xl">
              <div className="absolute -inset-3 rounded-[2rem] bg-[#fda36b]/20" />
              <img src={guitarPhoto} alt="Musician playing acoustic guitar with warm lighting" className="relative aspect-[4/3] w-full rounded-[1.5rem] object-cover shadow-[0_24px_50px_rgba(19,41,71,0.16)]" loading="lazy" />
              <div className="absolute -right-2 -top-5 rounded-xl bg-white px-4 py-3 shadow-xl sm:right-5"><p className="flex items-center gap-2 text-xs font-bold"><MapPin className="h-4 w-4 text-[#f08b13]" />North Shore, MA</p></div>
            </div>
          </div>
        </section>

        <section id="connect" className="scroll-mt-20 bg-[#05090e] px-5 py-16 text-white sm:py-24 lg:py-28">
          <div className="mx-auto max-w-3xl text-center">
            <Eyebrow light>The invitation</Eyebrow>
            <h2 className="mt-6 font-serif text-4xl font-black leading-[0.94] tracking-[-0.05em] sm:text-5xl lg:text-7xl">Pull up a chair.</h2>
            <div className="mx-auto mt-7 max-w-xl space-y-5 text-base leading-relaxed text-white/70 sm:text-lg">
              <p>Are you a musician who'd like to be part of Bond Sessions?</p>
              <p>Tell us a little about yourself, where we can hear your music, and why you'd like to join us.</p>
              <p>No formal applications. No auditions. Just an email to two guys who love coffee, music, and meeting interesting people.</p>
            </div>
            <a href={mailtoMusician} className="mt-10 inline-flex items-center gap-3 rounded-full bg-[#fda36b] px-7 py-4 text-xs font-black uppercase tracking-[0.15em] text-[#132947] transition duration-300 hover:-translate-y-1 hover:bg-[#ffbd8d] sm:px-8 sm:py-5 sm:text-sm">
              I'd Love to Come By Bond HQ
              <ArrowRight className="h-5 w-5" />
            </a>
            <div className="mt-8 flex flex-col items-center gap-2"><p className="text-sm text-white/45">Or email us directly:</p><a href={mailtoMusician} className="inline-flex items-center gap-2 break-all font-serif text-lg font-bold text-[#fda36b] transition hover:text-[#ffbd8d] sm:text-xl"><Mail className="h-5 w-5 shrink-0" />hello@thebondcoffee.com</a></div>
            <p className="mt-12 font-serif text-2xl italic text-white/80">See you over coffee.</p>
            <p className="mt-4 text-sm font-bold uppercase tracking-[0.16em] text-[#fda36b]">— Chris &amp; Adam</p>
            <p className="mt-2 text-xs uppercase tracking-[0.14em] text-white/40">Founders, Bond Coffee Roasters</p>
          </div>
        </section>

        <section className="bg-[#f7f4ee] px-5 py-14 text-center sm:py-20"><p className="font-serif text-2xl font-black tracking-[-0.04em] text-[#132947] sm:text-3xl">BOND COFFEE ROASTERS<span className="text-[#fda36b]">.</span></p><p className="mx-auto mt-4 max-w-md font-serif text-lg italic leading-relaxed text-[#132947]/60">Coffee brings us together. Music gives us something to talk about.</p></section>
      </main>

      <footer className="bg-[#0b131d] px-5 py-9 text-white/60 lg:px-10"><div className="mx-auto flex max-w-6xl flex-col items-center gap-5 sm:flex-row sm:items-end sm:justify-between"><div><p className="font-serif text-xl font-black text-white">Bond Coffee Roasters<span className="text-[#fda36b]">.</span></p><p className="mt-2 text-sm">Marblehead, Massachusetts</p></div><div className="flex flex-wrap items-center justify-center gap-5 text-xs font-bold uppercase tracking-[0.12em]"><Link to="/" className="transition hover:text-[#fda36b]">Visit Bond Coffee</Link><a href="https://www.instagram.com/thebondcoffee/" target="_blank" rel="noopener noreferrer" className="transition hover:text-[#fda36b]">Instagram</a><a href={mailtoMusician} className="transition hover:text-[#fda36b]">Contact</a></div></div></footer>
    </div>
  );
}
